import {
  Box,
  Button,
  Card,
  Heading,
  HStack,
  Image,
  List,
  SimpleGrid,
  Spinner,
  Text,
  VStack,
} from '@chakra-ui/react'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { ArrowLeft, Building2, Lightbulb, Target, Trophy } from 'lucide-react'
import { useState } from 'react'
import { customersApi } from '@/lib/api'
import { PageHeader } from '~/components/shell'

export const Route = createFileRoute('/_app/customers/$slug')({
  component: CustomerDetailPage,
  validateSearch: (search: Record<string, unknown>) => {
    return {
      from: (search.from as string) || '/',
    }
  },
})

function CustomerDetailPage() {
  const navigate = useNavigate()
  const { slug } = Route.useParams()
  const { from } = Route.useSearch()
  const [coverError, setCoverError] = useState(false)

  const {
    data: customer,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['customer', slug],
    queryFn: () => customersApi.getBySlug(slug),
  })

  // Loading state
  if (isLoading) {
    return (
      <Box maxW="1400px" mx="auto">
        <Button variant="ghost" onClick={() => navigate({ to: from })} mb={4}>
          <ArrowLeft size={18} />
          Back
        </Button>
        <VStack py={20} gap={4}>
          <Spinner size="xl" />
          <Text color="content.secondary">Loading customer details...</Text>
        </VStack>
      </Box>
    )
  }

  // Error or not found state
  if (error || !customer) {
    return (
      <Box maxW="1400px" mx="auto">
        <Button variant="ghost" onClick={() => navigate({ to: from })} mb={4}>
          <ArrowLeft size={18} />
          Back
        </Button>
        <Heading size="xl">Customer not found</Heading>
        <Text color="content.secondary" mt={2}>
          The customer you're looking for doesn't exist.
        </Text>
      </Box>
    )
  }

  return (
    <Box maxW="1400px" mx="auto">
      {/* Back Button */}
      <Button variant="ghost" mb={4} onClick={() => navigate({ to: from })}>
        <ArrowLeft size={18} />
        Back
      </Button>

      <PageHeader title={customer.name} subtitle={customer.industry} />

      {/* Hero image / logo */}
      <Card.Root overflow="hidden" mb={6}>
        <Box position="relative" h="280px">
          {customer.coverUrl && !coverError ? (
            <Image
              src={customer.coverUrl}
              alt={customer.name}
              objectFit="cover"
              w="100%"
              h="100%"
              onError={() => setCoverError(true)}
            />
          ) : (
            <Box w="100%" h="100%" bgGradient="linear(to-br, gray.400, gray.600)" />
          )}
          <Box
            position="absolute"
            bottom={6}
            left={6}
            bg="white"
            p={4}
            borderRadius="lg"
            shadow="lg"
          >
            {customer.logoUrl ? (
              <Image
                src={customer.logoUrl}
                alt={`${customer.name} Logo`}
                h="60px"
                objectFit="contain"
              />
            ) : (
              <Building2 size={60} color="var(--chakra-colors-gray-400)" />
            )}
          </Box>
        </Box>
      </Card.Root>

      {/* Description */}
      {customer.description && (
        <Card.Root mb={6}>
          <Card.Body p={6}>
            <Text fontSize="lg" color="content.secondary">
              {customer.description}
            </Text>
          </Card.Body>
        </Card.Root>
      )}

      {/* Three Column Grid */}
      <SimpleGrid columns={{ base: 1, md: 3 }} gap={6}>
        {/* Challenges */}
        {customer.challenge && (
          <Card.Root h="100%">
            <Card.Header>
              <HStack gap={3}>
                <Box p={2} bg="red.100" borderRadius="md">
                  <Target size={24} color="var(--chakra-colors-red-600)" />
                </Box>
                <Heading size="md">Challenges</Heading>
              </HStack>
            </Card.Header>
            <Card.Body>
              <Text color="content.secondary">{customer.challenge}</Text>
            </Card.Body>
          </Card.Root>
        )}

        {/* Solution */}
        {customer.solution && (
          <Card.Root h="100%">
            <Card.Header>
              <HStack gap={3}>
                <Box p={2} bg="blue.100" borderRadius="md">
                  <Lightbulb size={24} color="var(--chakra-colors-blue-600)" />
                </Box>
                <Heading size="md">Solution</Heading>
              </HStack>
            </Card.Header>
            <Card.Body>
              <Text color="content.secondary">{customer.solution}</Text>
            </Card.Body>
          </Card.Root>
        )}

        {/* Results */}
        {customer.results && customer.results.length > 0 && (
          <Card.Root h="100%">
            <Card.Header>
              <HStack gap={3}>
                <Box p={2} bg="green.100" borderRadius="md">
                  <Trophy size={24} color="var(--chakra-colors-green-600)" />
                </Box>
                <Heading size="md">Results</Heading>
              </HStack>
            </Card.Header>
            <Card.Body>
              <List.Root as="ul" listStyle="disc" pl={4} gap={2}>
                {customer.results.map((result) => (
                  <List.Item key={result}>
                    <Text color="content.secondary">{result}</Text>
                  </List.Item>
                ))}
              </List.Root>
            </Card.Body>
          </Card.Root>
        )}
      </SimpleGrid>
    </Box>
  )
}
